import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error(t('fillAllFields'));
      return;
    }

    setLoading(true);
    try {
      const userData = await login(email, password);
      toast.success(t('welcomeBack'));
      // Redirect admin to admin panel, regular users to home
      if (userData.is_admin) {
        navigate('/admin');
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || t('invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Welcome Banner */}
        <div className="bg-mrc-orange text-white text-center py-3 mb-6">
          <span className="font-heading text-xl italic">{t('welcome')}</span>
        </div>

        <div className="bg-white p-8 shadow-lg">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_mwenkomerce/artifacts/xt1gnrb8_IMG_1789.png" 
              alt="MRC Boutique Logo" 
              className="h-20 mx-auto mb-4"
            />
            <h1 className="font-heading text-3xl font-bold text-mrc-navy">
              MRC Boutique, LLC
            </h1>
            <p className="text-mrc-orange italic mt-1">{t('tagline')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pr-10"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-mrc-navy hover:bg-mrc-navy/90 text-white py-6 text-lg font-semibold uppercase tracking-wider rounded-full"
              data-testid="login-btn"
            >
              {loading ? t('loggingIn') : t('login')}
              {!loading && <LogIn className="ml-2 h-5 w-5" />}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/forgot-password" className="text-muted-foreground hover:text-mrc-navy text-sm">
              Bliye modpas ou?
            </Link>
          </div>

          <div className="mt-4 text-center">
            <p className="text-muted-foreground">
              {t('noAccount')}{' '}
              <Link to="/register" className="text-mrc-navy font-semibold hover:underline" data-testid="register-link">
                {t('registerNow')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
